import { t } from 'i18next';
import React, { FC, useCallback, useMemo } from 'react';

import SSelect, { SSelectItem } from 'Components/structural/SSelect/SSelect';
import { AudiencePlayerList } from 'Features/audience/pages/AudiencePlayerList/AudiencePlayerList';
import { AudienceScope, EntityType } from 'Features/audience/pages/AudienceGroupList/types';

import styles from './AudienceTargetingCampaign.style';

export type AudienceTargetingCampaignProps = {
  entityType: EntityType;
  entityId: number;
  value?: number;
  update: (value: number) => void;
  scope: AudienceScope;
  audienceUpdated: boolean;
  loading?: boolean;
  campaigns: { campaignId: number; title: string }[];
};

export const AudienceTargetingCampaign: FC<AudienceTargetingCampaignProps> = ({
  entityId,
  entityType,
  campaigns,
  value,
  update,
  scope,
  audienceUpdated,
  loading: loadingFromProps = false,
}) => {
  const campaignOptions = useMemo(() => {
    return campaigns.map((campaign) => ({
      id: campaign.campaignId,
      label: campaign.title,
      value: campaign.campaignId.toString(),
    }));
  }, [campaigns]);

  const handleSelectItem = useCallback(
    (item: SSelectItem) => {
      const option = campaignOptions.find((option) => option.value === item.value?.toString());
      if (option && parseInt(option.value, 10)) {
        update(parseInt(option.value, 10));
      }
    },
    [campaignOptions, update],
  );

  const selectedItem = useMemo(
    () => campaignOptions.find((option) => option.value === value?.toString()),
    [value, campaignOptions],
  );

  const selectItems = useMemo(
    () => (campaignOptions.length > 0 ? campaignOptions : [{ id: 0, label: 'loading', value: 'loading' }]),
    [campaignOptions],
  );

  return (
    <div style={styles.infoWrapper}>
      <div style={styles.title}>{t('activities:audience.campaign.title')}</div>
      <div style={styles.subtitle}>{t('activities:audience.campaign.subtitle')}</div>
      <div style={{ ...styles.subtitle, ...styles.subtitle2 }}>{t('activities:audience.campaign.subtitle_2')}</div>

      <SSelect
        canSearch
        placeholder={t('activities:audience.campaign.search_placeholder')}
        items={selectItems}
        onSelect={handleSelectItem}
        selected={selectedItem}
        canCreate={false}
      />

      <div style={styles.audienceTable}>
        <AudiencePlayerList
          entityId={entityId}
          entityType={entityType}
          scope={scope}
          audienceType="campaign"
          audienceHasBeenReset={audienceUpdated}
          loading={loadingFromProps}
        />
      </div>
    </div>
  );
};
